// src/components/AudioSelectorForEvents.js
import React, { useState, useRef } from 'react';
import { apiFetch } from '../../apiFetch';

function AudioSelectorForEvents({ audioGerado, onAudioSelected }) {
  const [origem, setOrigem] = useState('gerado');
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      alert('Por favor, selecione um arquivo de áudio válido');
      return;
    }

    setUploading(true);
    setUploadStatus({ variant: 'info', message: 'Enviando arquivo...' });

    try {
      const formData = new FormData();
      formData.append('audio', file);

      const response = await apiFetch('/api/upload-event-audio', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro no upload');
      }

      const data = await response.json();

      if (data.success) {
        const audioInfo = {
          fileName: data.fileName,
          audioRef: data.audioRef || data.fileName,
          audioUrl: data.url,
          originalName: data.originalName,
          normalized: data.normalized,
          level: data.level,
          origem: 'upload'
        };

        setUploadedFile(audioInfo);
        onAudioSelected(audioInfo);

        if (data.normalized) {
          setUploadStatus({
            variant: 'success',
            message: `Upload concluído! Normalizado para ${data.level} LUFS`
          });
        } else {
          setUploadStatus({
            variant: 'warning',
            message: 'Upload OK, mas não foi normalizado'
          });
        }
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadStatus({ variant: 'error', message: `Erro: ${error.message}` });
      setUploadedFile(null);
      onAudioSelected(null);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleOrigemChange = (novaOrigem) => {
    setOrigem(novaOrigem);
    
    if (novaOrigem === 'gerado' && audioGerado) {
      const audioRef = audioGerado.audioRef || audioGerado.fileName;
      onAudioSelected({
        fileName: audioGerado.fileName || audioRef,
        audioRef,
        audioUrl: audioGerado.previewUrl || null,
        origem: 'gerado'
      });
    } else if (novaOrigem === 'upload' && uploadedFile) {
      onAudioSelected(uploadedFile);
    } else {
      onAudioSelected(null);
    }
  };

  return (
    <div style={{
      padding: '15px',
      background: 'rgba(var(--brand-rgb), 0.1)',
      borderRadius: '10px',
      border: '1px solid rgba(var(--brand-rgb), 0.3)',
      marginBottom: '20px',
      color: 'var(--text-primary)'
    }}>
      <h4 style={{ margin: '0 0 15px 0', color: 'var(--brand-400)' }}>
        Selecionar Áudio do Evento
      </h4>

      {/* Seletor de origem */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        marginBottom: '15px'
      }}>
        <button
          onClick={() => handleOrigemChange('gerado')}
          disabled={!audioGerado}
          style={{
            padding: '12px',
            background: origem === 'gerado'
              ? 'var(--action)'
              : 'rgba(var(--brand-rgb), 0.2)',
            border: origem === 'gerado'
              ? '2px solid var(--brand-400)'
              : '1px solid rgba(var(--brand-rgb), 0.3)',
            borderRadius: '8px',
            color: 'white',
            cursor: audioGerado ? 'pointer' : 'not-allowed',
            opacity: audioGerado ? 1 : 0.5,
            fontSize: '14px',
            fontWeight: 'bold',
            transition: 'all 0.2s'
          }}
        >
          Áudio Gerado
          {!audioGerado && (
            <div style={{ fontSize: '11px', fontWeight: 'normal', marginTop: '5px' }}>
              (Crie um spot primeiro)
            </div>
          )}
        </button>

        <button
          onClick={() => handleOrigemChange('upload')}
          style={{
            padding: '12px',
            background: origem === 'upload'
              ? 'linear-gradient(135deg, var(--warning), #92600a)'
              : 'rgba(var(--warning-rgb), 0.2)',
            border: origem === 'upload'
              ? '2px solid var(--warning)'
              : '1px solid rgba(var(--warning-rgb), 0.3)',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            transition: 'all 0.2s'
          }}
        >
          Upload Externo
        </button>
      </div>

      {/* Área de upload */}
      {origem === 'upload' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={uploading}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '15px',
              background: uploading
                ? 'rgba(var(--neutral-300-rgb), 0.8)'
                : 'var(--gradient-success)',
              border: '2px dashed rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: 'white',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
          >
            {uploading ? 'Processando...' : 'Selecionar Arquivo'}
          </button>

          {uploadStatus && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              background: uploadStatus.variant === 'success'
                ? 'rgba(var(--success-rgb), 0.2)'
                : uploadStatus.variant === 'error'
                ? 'rgba(var(--error-rgb), 0.2)'
                : 'rgba(var(--warning-rgb), 0.2)',
              borderRadius: '6px',
              fontSize: '13px',
              textAlign: 'center',
              border: `1px solid ${
                uploadStatus.variant === 'success'
                  ? 'rgba(var(--success-rgb), 0.3)'
                  : uploadStatus.variant === 'error'
                  ? 'rgba(var(--error-rgb), 0.3)'
                  : 'rgba(var(--warning-rgb), 0.3)'
              }`
            }}>
              {uploadStatus.message}
            </div>
          )}

          {uploadedFile && (
            <div style={{
              marginTop: '10px',
              padding: '12px',
              background: 'rgba(var(--success-rgb), 0.2)',
              borderRadius: '8px',
              border: '1px solid rgba(var(--success-rgb), 0.3)'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
                Arquivo Selecionado
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {uploadedFile.originalName}
              </div>
              {uploadedFile.normalized && uploadedFile.level && (
                <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '5px' }}>
                  Normalizado para {uploadedFile.level} LUFS
                </div>
              )}
            </div>
          )}

          <div style={{
            marginTop: '10px',
            padding: '10px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '6px',
            fontSize: '11px',
            opacity: 0.85,
            color: 'var(--text-secondary)'
          }}>
            <strong>Dica:</strong> O sistema irá normalizar automaticamente seu arquivo para
            -14 LUFS (padrão de eventos), garantindo volume consistente.
          </div>
        </div>
      )}

      {/* Info do áudio gerado */}
      {origem === 'gerado' && audioGerado && (
        <div style={{
          padding: '12px',
          background: 'rgba(var(--brand-rgb), 0.2)',
          borderRadius: '8px',
          border: '1px solid rgba(var(--brand-rgb), 0.3)'
        }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
            Usando Áudio Gerado
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            {audioGerado.fileName}
          </div>
          <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '5px' }}>
            Já normalizado pelo sistema (-14 LUFS)
          </div>
        </div>
      )}
    </div>
  );
}

export default AudioSelectorForEvents;
